import { useMemo } from 'react';
import { Card, Loader } from '@securitize/reactjs-cp-ui';
import { CardContent, CardHeader, styled } from '@mui/material';
import { AccountStatus } from '../../../interfaces/AccountSummary';
import { Currency } from '../../../interfaces/Currency';
import { useInvestorSummary } from '../../../context/InvestorSummaryContext';
import { CashBalanceDetail } from './CashBalanceDetail';
import { OverviewTitle } from './Title';

type OverviewProps = {
  investorId: string;
};

const StyledCardContent = styled(CardContent)({
  minHeight: '104px',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const Overview = ({ investorId }: OverviewProps) => {
  const { summary, isLoading: isSummaryLoading } = useInvestorSummary();

  // TODO: We are filtering USD balance and cash balance because we are not using the other currencies
  // We should remove this filter when we start using the other currencies like EUR
  // Cash balances should be displayed dynamically based on the currency
  // https://securitize.atlassian.net/browse/ATS-2679
  const [usdCashBalance, usdBalance] = useMemo(() => {
    if (!summary) {
      return [];
    }

    const cashBalance = summary.cashBalances.find(
      (balance) => balance.currency === Currency.USD,
    );
    const balance = summary.balance?.find(
      (balance) => balance.currency === Currency.USD,
    );

    return [cashBalance, balance];
  }, [summary]);

  const isLoading = useMemo(
    () => isSummaryLoading || !usdCashBalance,
    [isSummaryLoading, usdCashBalance],
  );

  const usdConvertWithdrawDisabled = useMemo(
    () =>
      !usdCashBalance ||
      usdCashBalance.disabled ||
      usdCashBalance.status !== AccountStatus.CREATED,
    [usdCashBalance],
  );

  return (
    <Card>
      <CardHeader
        title={
          <OverviewTitle
            usdConvertWithdrawDisabled={usdConvertWithdrawDisabled}
            investorId={investorId}
          />
        }
      />
      <StyledCardContent>
        {isLoading && <Loader color="inherit" size={16} />}
        {!isLoading && (
          <CashBalanceDetail
            balance={usdBalance}
            cashBalance={usdCashBalance!}
          />
        )}
      </StyledCardContent>
    </Card>
  );
};
